import { useState, useEffect } from 'react';
import {
    Boolean, Calculation, Contact, ContactList, DateInput, Deadline, Dropdown,
    FileUpload, Instructions, MultiFile, MultiSelect, NumberInput, Subheader, 
    Text, TimeInput, SaveButton, SearchSelect
} from "./FieldComponents";
import { MultiVal } from './MultiVal';
import { CreateLead } from './CreateLead';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import '../styles/PFS.css';
import Alert from './Alert';
import MatterPublicFormField from './MatterPublicFormField';

const normalizeValueToIndex = (field, value) => {
    if (!field.options) return value;

    let options = [];
    try {
        options = Array.isArray(field.options)
            ? field.options
            : JSON.parse(field.options);
    } catch {
        return value;
    }

    if (typeof value === "string") {
        const index = options.findIndex(opt => opt.toLowerCase?.() === value.toLowerCase?.());
        return index !== -1 ? index : value;
    }

    return value;
};

const NotesField = ({ fieldId, value, onChange }) => {
    return (
        <div className="form-group nm conditional">
            <label className="subtext">Additional Notes</label>
            <Text 
                type="textarea" 
                placeholder="Enter additional notes here..." 
                value={value || ''} 
                onChange={onChange}
            />
        </div>
    );
};

const Field = ({ field, value, handleFieldChange, conditional = false, formData, fieldUpdates, fields, caseId, leadId, refreshAfterCalc = null, handleNotesChange, notesData }) => {
    if (!field) return null;

    const hasBooleanYesNo = (field.field_id === 10 && field.options && 
        (Array.isArray(field.options) 
            ? field.options.includes('Yes') && field.options.includes('No')
            : String(field.options).includes('Yes') && String(field.options).includes('No'))) || field.id === 507;

    const renderFieldInput = (field) => {
        switch (field.field_id) {
            case 1:
                return <Text type="text" placeholder={field.name} value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 2:
                return <Text type="textarea" placeholder={field.name} value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 3:
                return <NumberInput type="currency" value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 4:
                return <NumberInput type="percent" value={value} onChange={(val) => handleFieldChange(field.id, val)}/>;
            case 5:
                return <NumberInput type="number" value={value} onChange={(val) => handleFieldChange(field.id, val)}/>;
            case 6:
                return <DateInput value={String(value)} onChange={(val) => handleFieldChange(field.id, val)}/>;
            case 7:
                return <TimeInput value={value} onChange={(val) => handleFieldChange(field.id, val)}/>;
            case 8:
                return <Contact selectedContact={value} setSelectedContact={(contact) => handleFieldChange(field.id, contact?.id || null)} />;
            case 9:
                return <ContactList value={Array.isArray(value) ? value : []} onChange={(ids) => handleFieldChange(field.id, ids)} />;
            case 10:
                return <Dropdown 
                        options={field.options || "[]"} 
                        value={value} 
                        onChange={(index) => handleFieldChange(field.id, index)} 
                        marketing_list={field.id === 225} 
                    />;
            case 11:
                return <MultiSelect options={field.options || "[]"} value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 12:
                return <Boolean options={field.options || []} value={value != undefined ? Number(value) : 2} onChange={(selectedValue) => handleFieldChange(field.id, selectedValue)} />;
            case 13:
                return <Subheader title={field.name} />;
            case 14:
                return <Instructions instructions={field.name}/>;
            case 15:
                return <FileUpload value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 16:
                return <MultiFile value={value} onChange={(val) => handleFieldChange(field.id, val)} />;
            case 17:
                return (
                    <Calculation
                        options={field.options}
                        fieldId={field.id}
                        case_id={caseId}
                        fields={fields}
                        formData={formData}
                        fieldUpdates={fieldUpdates}
                        onChange={(val) => {
                            if (typeof refreshAfterCalc === 'function') {
                                refreshAfterCalc(field.id, val);
                            } else {
                                console.error('refreshAfterCalc is not a function:', typeof refreshAfterCalc);
                                handleFieldChange(field.id, val);
                            }
                        }}
                    />
                );                
            case 18:
                return <SearchSelect value={value} onChange={(val) => handleFieldChange(field.id, val)} options={field.options} />;
            case 20:
                const parsedValue = typeof value === 'string' ? JSON.parse(value || '{}') : value || {};
                return <Deadline 
                            value={{ due: parsedValue.due || '', done: parsedValue.done || '' }}
                            title={field.name}
                            onChange={(updated) => handleFieldChange(field.id, {
                                ...parsedValue,
                                ...updated
                            })}
                        />;
            case 22:
                return <SearchSelect
                            value={value}
                            onChange={(val) => handleFieldChange(field.id, val)}
                            options={field.options}
                        />;
            case 26:
                return <MultiVal
                            value={value}
                            options={field.options || '[]'}
                            fieldName={field.name}
                            onChange={(val) => handleFieldChange(field.id, val)}
                        />;
            case 28:
                return <MatterPublicFormField
                            field={field}
                            leadId={leadId}
                        />;
            default:
                return null;
        }
    };

    return (
        <>
            {![13, 14, 20, 26].includes(field.field_id) ? (
                <div className={`form-group nm ${conditional === false ? '' : 'conditional'}`}>
                    <label className='subtext'>{field.name}</label>
                    {renderFieldInput(field)}
                </div>
            ) : renderFieldInput(field)}
            
            {hasBooleanYesNo && (
                <NotesField 
                    fieldId={field.id} 
                    value={notesData[field.id] || ''} 
                    onChange={(val) => handleNotesChange(field.id, val)} 
                />
            )}
        </>
    );
};

export const PFS = () => {
    const [pfsLeads, setPfsLeads] = useState([]);
    const [pfsCount, setPfsCount] = useState(0);
    const [selectedLead, setSelectedLead] = useState(null);
    const [fields, setFields] = useState([]);
    const [fieldUpdates, setFieldUpdates] = useState([]);
    const [formData, setFormData] = useState({});
    const [dataChanged, setDataChanged] = useState(false);
    const [sectionName, setSectionName] = useState('');
    const [loading, setLoading] = useState(false);
    const [showCreateLead, setShowCreateLead] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);
    const [notesData, setNotesData] = useState({});
    const [notesChanged, setNotesChanged] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    
    useEffect(() => {
        const fetchCurrentUser = async () => {
            if (process.env.NODE_ENV === 'development') {
                setCurrentUser({ user_id: 12, user: 'devuser', email: 'devuser@casedb.co' });
                return;
            }
            try {
                const response = await fetch('https://api.casedb.co/user.php', {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                const data = await response.json();
                if (data.success) {
                    setCurrentUser(data.user);
                }
            } catch (error) {
                console.error("Error fetching current user:", error);
            }
        };
        
        fetchCurrentUser();

        document.title = "PFS | CaseDB";
    }, []);
    
    useEffect(() => {
        fetchPfsLeads();
    }, []);

    const fetchPfsLeads = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/leads.php?pfs`, { credentials: 'include' });
            const data = await response.json();
            if (data.success && data.pfs_leads) {
                setPfsLeads(data.pfs_leads);
                setPfsCount(data.pfs_count || 0);
            }
        } catch (error) {
            console.error("Error fetching PFS leads:", error);
        }
    };

    const handleLeadChange = (leadId) => {
        if (leadId) {
            setSelectedLead(leadId);
            fetchFields(leadId);
            fetchNotes(leadId);
        } else {
            setSelectedLead(null);
            setFields([]);
            setFieldUpdates({});
            setFormData({});
            setNotesData({});
        }
    };

    const fetchFields = async (leadId) => {
        setLoading(true);
        try {
            const res = await fetch(
                `https://api.casedb.co/custom_fields.php?section_id=47&template_id=7&lead_id=${leadId}&${Date.now()}`,
                { credentials: 'include' }
            );
            const data = await res.json();
            
            const sortedFields = (data.custom_fields || []).sort((a, b) => a.order_id - b.order_id);
            setFields(sortedFields);
            setSectionName(data.section_name || "PFS Fields");
            mergeFieldValues(sortedFields, data.field_updates || []);
        } catch (error) {
            console.error("Error fetching PFS fields:", error);
        } finally {
            setLoading(false);
        }
    };

    const mergeFieldValues = (customFields, updates) => {
        const flatMap = {};
        
        updates.forEach(update => {
            if (update.group_id === 0) {
                Object.entries(update).forEach(([key, val]) => {
                    if (key !== 'group_id') {
                        const fieldDef = customFields.find(f => String(f.id) === String(key));
                
                        if (String(val) === String(key)) return;
                
                        if (fieldDef?.field_id === 17) {
                            const num = parseFloat(val);
                            flatMap[key] = isNaN(num) ? 0 : parseFloat(num.toFixed(2));
                        } else {
                            flatMap[key] = val;
                        }
                    }
                });
            }
        });
    
        setFieldUpdates(flatMap);
    };

    const handleFieldChange = (fieldId, value) => {
        setFormData((prev) => ({ ...prev, [fieldId]: value }));
        setDataChanged(true);
    };

    const refreshAfterCalc = (fieldId, value) => {
        handleFieldChange(fieldId, value);
    };

    const fetchNotes = async (leadId) => {
        try {
            const response = await fetch(`https://api.casedb.co/pfs_notes.php?lead_id=${leadId}`);
            const data = await response.json();
            
            if (data.success && data.notes) {
                const notesObj = {};
                data.notes.forEach(note => {
                    notesObj[note.field_id] = note.data;
                });
                setNotesData(notesObj);
            }
        } catch (error) {
            console.error("Error fetching PFS notes:", error);
        }
    };

    const handleNotesChange = (fieldId, value) => {
        setNotesData((prev) => ({ ...prev, [fieldId]: value }));
        setNotesChanged(true);
    };

    const saveFields = async () => {
        if (!selectedLead) return;
        
        const data = {
            lead_id: selectedLead,
            field_values: Object.entries(formData).reduce((acc, [id, value]) => {
                acc[id] = (typeof value === 'object') ? JSON.stringify(value) : value;
                return acc;
            }, {})
        };
        
        try {
            await fetch('https://api.casedb.co/leads.php', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    id: selectedLead,
                    pfs: 1
                })
            });
            
            const response = await fetch('https://api.casedb.co/custom_fields.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(data)
            });
            
            const result = await response.json();
            
            if (notesChanged) {
                const notesPayload = {
                    lead_id: selectedLead,
                    notes: Object.entries(notesData).map(([field_id, data]) => ({
                        field_id,
                        data
                    }))
                };
                
                const notesResponse = await fetch('https://api.casedb.co/pfs_notes.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify(notesPayload)
                });
                
                const notesResult = await notesResponse.json();
                if (!notesResult.success) {
                    console.error("Failed to update notes:", notesResult.message);
                }
            }
            
            if (result.success) {
                setDataChanged(false);
                setNotesChanged(false);
                fetchFields(selectedLead);
                fetchNotes(selectedLead);
                setShowAlert({ message: "Fields updated successfully!", type: "success" });
            } else {
                console.error("Failed to update fields:", result.message);
                setShowAlert({ message: "Fields failed to update!", type: "error" });

            }
        } catch (error) {
            console.error("Error updating fields:", error);
            setShowAlert({ message: "Error updating fields!", type: "error" });
        }
    };

    const handleLeadCreated = () => {
        setShowCreateLead(false);
        fetchPfsLeads();
    };

    const resolveCalculationValue = (fieldId, visited = new Set()) => {
        if (visited.has(fieldId)) {
            console.warn(`Circular dependency detected for field ID: ${fieldId}`);
            return 0;
        }
    
        visited.add(fieldId);
    
        const field = fields.find(f => f.id === fieldId);
        if (!field || field.field_id !== 17) return getFieldValue(field);
    
        const options = typeof field.options === 'string' ? JSON.parse(field.options) : field.options;
        const dependentFieldIds = JSON.parse(options?.field_ids || '[]');
        const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
        let expression = options?.rule || '';
    
        dependentFieldIds.forEach((id, idx) => {
            let value = resolveCalculationValue(id, visited);
            if (value === '' || value === null || value === undefined) {
                const operatorBefore = expression[expression.indexOf(letters[idx]) - 1];
                value = operatorBefore === '*' || operatorBefore === '/' ? 1 : 0;
            }
            expression = expression.replaceAll(letters[idx], value);
        });
    
        try {
            if (!expression || /[^0-9+\-*/(). ]/.test(expression) || /[+\-*/]$/.test(expression)) {
                throw new SyntaxError(`Invalid or incomplete expression: "${expression}"`);
            }
    
            const result = new Function(`return ${expression}`)();
            return typeof result === 'number' ? parseFloat(result.toFixed(2)) : 0;
        } catch (e) {
            console.error(`Error evaluating calculation for field ID: ${fieldId}`, e, { expression });
            return 0;
        }
    };
    
    const getFieldValue = (field) => {
        if (!field) return '';
        
        const val = formData[field.id];
        if (val !== undefined && val !== null) return val;

        const stored = fieldUpdates?.[field.id];
        if (stored !== undefined) return stored;

        if (field.field_id === 17) {
            return resolveCalculationValue(field.id);
        }

        if (field.field_id === 12) return 2;

        return '';
    };

    const shouldDisplay = (field) => {
        if (Number(field.hidden) === 2 || Number(field.obsolete) === 2) return false;
    
        // Parse display_when/display_multi (check display_multi first)
        let fieldIdToCheck = null;
        try {
            const toparse = field.display_multi || field.display_when;
            const parsed = JSON.parse(String(toparse));
            if (Array.isArray(parsed)) {
                fieldIdToCheck = parsed[0];
            } else {
                fieldIdToCheck = parseInt(parsed);
            }
        } catch (e) {
            fieldIdToCheck = parseInt(field.display_multi || field.display_when);
        }
        
        const controllingField = fields.find(f => f.id === fieldIdToCheck);
        if (!field.display_when && !field.display_multi || !controllingField) return true;
    
        let controllingValue = getFieldValue(controllingField);

        if (field.is_answered === '[!]') {
            return controllingValue === null || controllingValue === undefined || controllingValue === "";
        }
    
        if (field.is_answered === null || field.is_answered === "") {
            return controllingValue !== null && controllingValue !== undefined && controllingValue !== "";
        }
    
        controllingValue = normalizeValueToIndex(controllingField, controllingValue);
    
        let parsedAnswers = [];
        try {
            const parsed = JSON.parse(field.is_answered);
            if (Array.isArray(parsed)) parsedAnswers = parsed.map(v => String(v));
            else parsedAnswers = [String(parsed)];
        } catch {
            parsedAnswers = String(field.is_answered).split(',').map(v => v.trim());
        }
        
        const isNegated = parsedAnswers.every(answer => String(answer).startsWith('!'));
        const cleanAnswers = parsedAnswers.map((v) => String(v).replace(/^!/, ''));

        if (isNegated) {
            return !cleanAnswers.includes(String(controllingValue));
        } else {
            return cleanAnswers.includes(String(controllingValue));
        }
    };

    const renderFields = () => {
        const dependencyMap = {};
        fields.forEach(field => {
            if (field.display_when) {
                // Parse display_when to get first field ID for dependency mapping
                let fieldIdForMapping = null;
                try {
                    const parsed = JSON.parse(String(field.display_when));
                    if (Array.isArray(parsed)) {
                        fieldIdForMapping = parsed[0];
                    } else {
                        fieldIdForMapping = parseInt(parsed);
                    }
                } catch (e) {
                    fieldIdForMapping = parseInt(field.display_when);
                }
                
                if (fieldIdForMapping) {
                    if (!dependencyMap[fieldIdForMapping]) dependencyMap[fieldIdForMapping] = [];
                    dependencyMap[fieldIdForMapping].push(field);
                }
            }
        });

        const rendered = new Set();
        const output = [];
        let currentSubSection = [];
        let currentInstructionGroup = [];

        const flushSubSection = () => {
            if (currentSubSection.length > 0) {
                if (currentSubSection[0].props.field.field_id === 13) {
                    output.push(
                        <div key={`frag-${output.length}`}>
                            {currentSubSection}
                        </div>
                    );
                } else {
                    output.push(
                        <div className="sub-section" key={`sub-${output.length}`}>
                            {currentSubSection}
                        </div>
                    );
                }
                currentSubSection = [];
            }
        };

        const flushInstructionGroup = () => {
            if (currentInstructionGroup.length > 0) {
                output.push(
                    <div className="instruction-group" key={`instruction-${output.length}`}>
                        {currentInstructionGroup}
                    </div>
                );
                currentInstructionGroup = [];
            }
        };

        fields
            .filter(f => shouldDisplay(f))
            .sort((a, b) => a.order_id - b.order_id)
            .forEach((field, index) => {
                if (rendered.has(field.id)) return;

                const children = dependencyMap[field.id] || [];
                const visibleChildren = children.filter(shouldDisplay);
                
                const fieldEl = (
                    <Field
                        key={`${field.id}-${index}`}
                        field={field}
                        value={getFieldValue(field)}
                        handleFieldChange={handleFieldChange}
                        conditional={false}
                        formData={formData}
                        fieldUpdates={fieldUpdates}
                        fields={fields}
                        caseId={selectedLead}
                        leadId={selectedLead}
                        refreshAfterCalc={refreshAfterCalc}
                        handleNotesChange={handleNotesChange}
                        notesData={notesData}
                    />
                );
                
                const childrenEls = visibleChildren.map(child => (
                    <Field
                        key={`${child.id}-${index}-child`}
                        field={child}
                        value={getFieldValue(child)}
                        handleFieldChange={handleFieldChange}
                        conditional={true}
                        formData={formData}
                        fieldUpdates={fieldUpdates}
                        fields={fields}
                        caseId={selectedLead}
                        leadId={selectedLead}
                        refreshAfterCalc={refreshAfterCalc}
                        handleNotesChange={handleNotesChange}
                        notesData={notesData}
                    />
                ));

                rendered.add(field.id);
                
                if (visibleChildren.length > 0) {
                    children.forEach(child => {
                        if (shouldDisplay(child)) {
                            rendered.add(child.id);
                        }
                    });
                }

                if (field.field_id === 13) {
                    flushInstructionGroup();
                    flushSubSection();
                    currentSubSection.push(fieldEl, ...childrenEls);
                    flushSubSection();
                } else if (field.field_id === 14) {
                    flushSubSection();
                    currentInstructionGroup.push(fieldEl, ...childrenEls);
                } else {
                    flushInstructionGroup();
                    currentSubSection.push(fieldEl, ...childrenEls);
                }
            });

        flushSubSection();
        flushInstructionGroup();

        return output;
    };

    const exportToPdf = async () => {
        if (!selectedLead) return;
        
        setExportLoading(true);
        try {
            const allFieldData = { ...fieldUpdates, ...formData };
            
            const selectedLeadData = pfsLeads.find(l => l.id === selectedLead);
            const contactName = selectedLeadData?.contact_name || '';
            
            if (!window.PDFLib) {
                const script = document.createElement('script');
                script.src = 'https://unpkg.com/pdf-lib@1.17.1/dist/pdf-lib.min.js';
                script.async = true;
                
                await new Promise((resolve, reject) => {
                    script.onload = resolve;
                    script.onerror = reject;
                    document.head.appendChild(script);
                });
            }
            
            if (!window.PDFLib) {
                throw new Error('PDF-Lib library failed to load');
            }
            
            const { PDFDocument } = window.PDFLib;
            
            const formPdfResponse = await fetch('https://api.casedb.co/read-pdf.php', {
                method: 'GET',
                cache: 'no-cache',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/pdf',
                }
            });
            
            if (!formPdfResponse.ok) {
                console.error('PDF fetch failed with status:', formPdfResponse.status);
                throw new Error(`Failed to fetch PDF template: ${formPdfResponse.status} ${formPdfResponse.statusText}`);
            }
            
            const formPdfBytes = await formPdfResponse.arrayBuffer();
            
            if (!formPdfBytes || formPdfBytes.byteLength === 0) {
                throw new Error('PDF template is empty');
            }
            
            const pdfDoc = await PDFDocument.load(formPdfBytes);
            const form = pdfDoc.getForm();
            
            const safeSetField = (fieldName, value, fieldType = 'text') => {
                try {
                    const fieldNameStr = String(fieldName);
                    
                    if (fieldType === 'checkbox') {
                        try {
                            const field = form.getCheckBox(fieldNameStr);
                            field.check();
                            return true;
                        } catch (e) {
                            console.warn(`Checkbox ${fieldNameStr} not found:`, e);
                            return false;
                        }
                    } else {
                        try {
                            const field = form.getTextField(fieldNameStr);
                            field.setText(String(value));
                            return true;
                        } catch (e) {
                            try {
                                const fieldTypes = ['comboBox', 'listBox', 'radioGroup'];
                                for (const type of fieldTypes) {
                                    try {
                                        const getterMethod = `get${type.charAt(0).toUpperCase() + type.slice(1)}`;
                                        if (form[getterMethod]) {
                                            const altField = form[getterMethod](fieldNameStr);
                                            if (altField && altField.select) {
                                                altField.select(String(value));
                                                return true;
                                            }
                                        }
                                    } catch (innerErr) {

                                    }
                                }
                            } catch (altErr) {

                            }
                            
                            console.warn(`Text field ${fieldNameStr} not found or error:`, e);
                            return false;
                        }
                    }
                } catch (e) {
                    console.warn(`Field operation failed for ${fieldName}:`, e);
                    return false;
                }
            };
            
            const parseMultiSelectValue = (value) => {
                if (Array.isArray(value)) {
                    return value;
                }
                
                if (typeof value === 'string' && value.trim().startsWith('[') && value.trim().endsWith(']')) {
                    try {
                        return JSON.parse(value);
                    } catch (e) {
                        try {
                            const cleanString = value.replace(/\[|\]/g, '').trim();
                            if (cleanString === '') return [];
                            
                            return cleanString.split(',').map(item => {
                                const trimmed = item.trim();
                                const num = Number(trimmed);
                                return isNaN(num) ? trimmed : num;
                            });
                        } catch (e2) {
                            console.warn('Failed to parse multi-select value:', value);
                            return [];
                        }
                    }
                }
                
                return [];
            };
            
            const checkMultiselectCheckboxes = (fieldId, values) => {
                if (!values || values.length === 0) return false;
                
                let success = false;
                
                const valueArray = Array.isArray(values) ? values : [values];
                                
                valueArray.forEach(val => {
                    try {
                        const checkboxName = `${fieldId}.${val}`;
                        const checkbox = form.getCheckBox(checkboxName);
                        checkbox.check();
                        success = true;
                    } catch (e) {
                        console.warn(`Failed to check ${fieldId}.${val}, trying alternatives`);
                        
                        const alternateFormats = [
                            `${fieldId}_${val}`,
                            `${fieldId}[${val}]`,
                            `${fieldId}(${val})`,
                            `${fieldId}-${val}`
                        ];
                        
                        for (const format of alternateFormats) {
                            try {
                                const checkbox = form.getCheckBox(format);
                                checkbox.check();
                                success = true;
                                break;
                            } catch (altErr) {

                            }
                        }
                    }
                });
                
                return success;
            };

            safeSetField('contact_name', contactName);
            safeSetField('current_date', new Date().toLocaleDateString());
            
            const visibleFields = fields
                .filter(f => shouldDisplay(f))
                .sort((a, b) => a.order_id - b.order_id);
                        
            try {
                const formFields = form.getFields();
                console.log(`PDF has ${formFields.length} form fields`);
                const fieldNames = formFields.map(field => field.getName());
                console.log('Available field names:', fieldNames);
            } catch (e) {
                console.warn('Could not get form field names:', e);
            }
            
            visibleFields.forEach(field => {
                const fieldId = field.id;
                const value = allFieldData[fieldId];
                
                if (value === undefined || value === null) return;
                
                try {
                    if ([13, 14].includes(field.field_id)) return;
                    
                    switch (field.field_id) {
                        case 10:
                            try {
                                const options = Array.isArray(field.options) 
                                    ? field.options 
                                    : JSON.parse(field.options || '[]');
                                
                                const selectedIndex = Number(value);
                                if (!isNaN(selectedIndex)) {
                                    safeSetField(fieldId, options[selectedIndex] || String(value));
                                    
                                    const checkboxName = `${fieldId}.${selectedIndex}`;
                                    try {
                                        const checkboxField = form.getCheckBox(checkboxName);
                                        checkboxField.check();
                                    } catch (e) {
                                        console.warn(`Checkbox ${checkboxName} not found, using text field only`);
                                    }
                                }
                            } catch (e) {
                                console.warn(`Error processing dropdown ${fieldId}:`, e);
                                safeSetField(fieldId, String(value));
                            }
                            break;
                        
                        case 12:
                            try {
                                const boolValue = Number(value);
                                
                                const textValue = boolValue === 1 ? 'Yes' : boolValue === 0 ? 'No' : 'N/A';
                                safeSetField(fieldId, textValue);
                                
                                if (boolValue === 1 || boolValue === 0) {
                                    const checkboxName = `${fieldId}.${boolValue}`;
                                    try {
                                        const checkbox = form.getCheckBox(checkboxName);
                                        checkbox.check();
                                    } catch (e) {
                                        console.warn(`Checkbox ${checkboxName} not found`);
                                    }
                                }
                                
                                const notes = notesData[fieldId];
                                if (notes) {
                                    safeSetField(`${fieldId}.Add`, notes);
                                }
                            } catch (e) {
                                console.warn(`Error processing boolean field ${fieldId}:`, e);
                            }
                            break;
                            
                        case 11:
                            try {
                                const options = Array.isArray(field.options) 
                                    ? field.options 
                                    : JSON.parse(field.options || '[]');
                                
                                const multiSelectValues = parseMultiSelectValue(value);
                                
                                if (multiSelectValues.length > 0) {
                                    const displayValue = multiSelectValues
                                        .map(index => options[index])
                                        .filter(Boolean)
                                        .join(', ');
                                    
                                    safeSetField(fieldId, displayValue);
                                                                        
                                    let success = checkMultiselectCheckboxes(fieldId, multiSelectValues);
                                    
                                    if (!success) {
                                        const extractedOptionIds = [];
                                        multiSelectValues.forEach(idx => {
                                            const option = options[idx];
                                            if (typeof option === 'string') {
                                                const match = option.match(/\[(\d+)\]/);
                                                if (match && match[1]) {
                                                    extractedOptionIds.push(match[1]);
                                                }
                                            }
                                        });
                                        
                                        if (extractedOptionIds.length > 0) {
                                            success = checkMultiselectCheckboxes(fieldId, extractedOptionIds);
                                        }
                                    }
                                    
                                    if (!success) {
                                        try {
                                            const formFields = form.getFields();
                                            const relatedCheckboxes = formFields
                                                .filter(f => f.getName().startsWith(`${fieldId}.`))
                                                .map(f => f.getName());
                                                                                        
                                            multiSelectValues.forEach(val => {
                                                const matchingFields = relatedCheckboxes.filter(name => 
                                                    name.endsWith(`.${val}`) || name.includes(`.${val}.`)
                                                );
                                                
                                                matchingFields.forEach(fieldName => {
                                                    try {
                                                        const checkbox = form.getCheckBox(fieldName);
                                                        checkbox.check();
                                                        success = true;
                                                    } catch (e) {
                                                        console.warn(`Failed to check matched field ${fieldName}:`, e);
                                                    }
                                                });
                                            });
                                            
                                            if (!success) {
                                                multiSelectValues.forEach(idx => {
                                                    const optionText = options[idx];
                                                    if (optionText) {
                                                        const sanitized = optionText.replace(/\W+/g, '_').toLowerCase();
                                                        
                                                        relatedCheckboxes.forEach(checkboxName => {
                                                            const nameParts = checkboxName.split('.');
                                                            const lastPart = nameParts[nameParts.length - 1].toLowerCase();
                                                            
                                                            if (lastPart === sanitized || 
                                                                lastPart === String(idx) || 
                                                                checkboxName.toLowerCase().includes(sanitized)) {
                                                                try {
                                                                    const checkbox = form.getCheckBox(checkboxName);
                                                                    checkbox.check();
                                                                    success = true;
                                                                } catch (e) {

                                                                }
                                                            }
                                                        });
                                                    }
                                                });
                                            }
                                        } catch (e) {
                                            console.warn('Error finding related checkboxes:', e);
                                        }
                                    }
                                } else {
                                    safeSetField(fieldId, '');
                                }
                            } catch (e) {
                                console.warn(`Error processing multi-select field ${fieldId}:`, e);
                                safeSetField(fieldId, String(value));
                            }
                            break;
                        
                        default:
                            let displayValue = '';
                            
                            if (field.field_id === 3) {
                                displayValue = `$${parseFloat(value).toFixed(2)}`;
                            } else if (field.field_id === 4) {
                                displayValue = `${parseFloat(value).toFixed(2)}%`;
                            } else if (field.field_id === 20) {
                                try {
                                    const deadlineValue = typeof value === 'object' ? value : JSON.parse(value || '{}');
                                    displayValue = [
                                        deadlineValue.due ? `Due: ${deadlineValue.due}` : '',
                                        deadlineValue.done ? `Done: ${deadlineValue.done}` : ''
                                    ].filter(Boolean).join(', ');
                                } catch (e) {
                                    displayValue = String(value);
                                }
                            } else if (field.field_id === 11) {
                                try {
                                    const options = Array.isArray(field.options) 
                                        ? field.options 
                                        : JSON.parse(field.options || '[]');
                                    
                                    if (Array.isArray(value)) {
                                        displayValue = value
                                            .map(index => options[index])
                                            .filter(Boolean)
                                            .join(', ');
                                            
                                        value.forEach(selectedIndex => {
                                            try {
                                                const checkboxName = `${fieldId}.${selectedIndex}`;
                                                const checkboxField = form.getCheckBox(checkboxName);
                                                checkboxField.check();
                                            } catch (e) {

                                            }
                                        });
                                    } else {
                                        displayValue = String(value);
                                    }
                                } catch (e) {
                                    displayValue = String(value);
                                }
                            } else {
                                displayValue = typeof value === 'object' 
                                    ? JSON.stringify(value) 
                                    : String(value);
                            }
                            
                            safeSetField(fieldId, displayValue);
                    }
                } catch (error) {
                    console.warn(`Error processing field ${fieldId}:`, error);
                    try {
                        safeSetField(fieldId, String(value));
                    } catch (e) {
                        console.warn(`Failed fallback for field ${fieldId}`);
                    }
                }
            });

            const pdfBytes = await pdfDoc.save();
            
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const link = document.createElement('a');
            const downloadUrl = URL.createObjectURL(blob);
            link.href = downloadUrl;
            link.download = `${String(pfsLeads.find(lead => lead.id === selectedLead).contact_name).replace(/\s+/g, ' ').trim()} - Uber Sexual Assault PFS.pdf`;
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);
            
        } catch (error) {
            console.error("Error exporting to PDF:", error);
            alert("Failed to export PDF. Please try again or use the table export.");
            
            try {
                const allFieldData = { ...fieldUpdates, ...formData };
                
                const selectedLeadData = pfsLeads.find(l => l.id === selectedLead);
                const contactName = selectedLeadData?.contact_name || '';
                
                const doc = new jsPDF();
                
            } catch (fallbackError) {
                console.error("Fallback PDF generation also failed:", fallbackError);
            }
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <div className='page-container' id='pfs'>
            <div className='toc-header'>
                <h2>PFS Form</h2>
                <div className='toc-header-actions'>
                    <button className='action alt' onClick={() => setShowCreateLead(true)}>Create New Lead</button>
                    {selectedLead && (
                        <>
                            <button 
                                className='action alt' 
                                onClick={exportToPdf} 
                                disabled={exportLoading}
                            >
                                {exportLoading ? 'Exporting...' : 'Export to PDF'}
                            </button>
                            {(dataChanged || notesChanged) && (
                                <button className='action' onClick={saveFields}>Save</button>
                            )}
                        </>
                    )}
                </div>
                {showAlert && <Alert message={showAlert.message} type={showAlert.type} onClose={() => setShowAlert(null)} />}
            </div>

            <div className='pfs-select-container'>
                <SearchSelect 
                    options={Object.fromEntries(pfsLeads.sort((a, b) => Number(b.id) - Number(a.id)).map(l => [l.id, l.contact_name]))} 
                    value={selectedLead} 
                    onChange={handleLeadChange} 
                    placeholder="Select a lead..."
                    small
                />
                <span className='subtext'>PFS Leads: {pfsCount}</span>
            </div>
            {loading ? (
                <div>Loading fields...</div>
            ) : selectedLead ? (
                <div className="case-section">{renderFields()}</div>
            ) : (
                <div className="pfs-info">Please select a lead to view PFS form or create a new lead</div>
            )}

            {showCreateLead && currentUser && (
                <CreateLead 
                    user={currentUser} 
                    setCreateLead={setShowCreateLead}
                    onLeadCreated={handleLeadCreated}
                />
            )}
        </div>
    );
};
